import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { App } from './app/app';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
  InjectionToken,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withDebugTracing } from '@angular/router';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { defaultHeadersInterceptor } from './app/default-headers-interceptor';
import { Home } from './app/home/home';
import { ProjectDetailComponent } from './app/project-detail/project-detail.component';
import * as apiResolvers from './app/api-resolver';
import { environment } from './environments/environment';
import { authGuard } from './app/auth-guard';
import { readOnlyGuard } from './app/read-only-guard';
import { Debug } from './app/debug/debug';
import { NewProjectDialog } from './app/new-project-dialog/new-project-dialog';
import { EditReports } from './app/edit-reports/edit-reports';
import { InviteParticipants } from './app/invite-participants/invite-participants';
import { GenerateIds } from './app/generate-ids/generate-ids';
import { ProjectList } from './app/project-list/project-list';
import { projectPackages, projectParticipants, projectReports } from './app/api-resolver';
import { ParticipantListComponent } from './app/participant-list/participant-list.component';
import { SelectedParticipantComponent } from './app/participant-list/selected-participant/selected-participant.component';
import { EditReportSettings } from './app/edit-report-settings/edit-report-settings';
import { retryInterceptor } from './app/retry-interceptor';
import { reportFallbackInterceptor } from './app/report-package-interceptor';
import { GlobalSearch } from './app/global-search/global-search';

export const ACTIVE_CLIENT = new InjectionToken<string>('activeClient');

// Register Spanish locale data and alias it to 'es-ES' used in angular.json
registerLocaleData(localeEs, 'es-ES');

bootstrapApplication(App, {
  providers: [
    provideAppInitializer(async () => {
      sessionStorage.removeItem('activeClient');
      const oauth = inject(OAuthService);
      oauth.configure(environment.oidc);
      await oauth.loadDiscoveryDocumentAndLogin();
      oauth.setupAutomaticSilentRefresh();
    }),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      [
        { path: 'callback.html', redirectTo: '/' },
        { path: 'debug', component: Debug },
        {
          path: '',
          canActivate: [authGuard],
          component: Home,
          resolve: { projects: apiResolvers.projects, assessments: apiResolvers.assessments },
          children: [
            {
              path: 'global-search',
              component: GlobalSearch,
            },
            {
              path: '',
              component: ProjectList,
              children: [
                {
                  path: 'new-project',
                  component: NewProjectDialog,
                  canActivate: [readOnlyGuard],
                  resolve: {
                    existingProjects: apiResolvers.projects,
                  },
                },
              ],
            },
            {
              path: 'projects/:projectId',
              component: ProjectDetailComponent,
              resolve: {
                project: apiResolvers.project,
                reports: projectReports,
                packages: projectPackages,
              },
              children: [
                {
                  path: 'rename',
                  component: NewProjectDialog,
                  canActivate: [readOnlyGuard],
                  resolve: {
                    existingProjects: apiResolvers.projects,
                  },
                },
                { path: 'invite', component: InviteParticipants, canActivate: [readOnlyGuard] },
                { path: 'generate-ids', component: GenerateIds, canActivate: [readOnlyGuard] },
                {
                  path: 'reports',
                  component: EditReports,
                  canActivate: [readOnlyGuard],
                  resolve: {
                    clientReports: apiResolvers.clientReports,
                    clientPackages: apiResolvers.clientPackages,
                  },
                },
                {
                  path: 'reports/:reportId',
                  component: EditReportSettings,
                  canActivate: [readOnlyGuard],
                  resolve: {
                    languages: apiResolvers.reportLanguages,
                    norms: apiResolvers.reportNorms,
                    report: apiResolvers.report,
                  },
                },
              ],
            },
            {
              path: 'projects/:projectId/participants',
              component: ParticipantListComponent,
              resolve: {
                project: apiResolvers.project,
                participants: projectParticipants,
                assessments: apiResolvers.projectAssessments,
                reports: projectReports,
                packages: projectPackages,
              },
              children: [
                {
                  path: ':participantId',
                  component: SelectedParticipantComponent,
                  resolve: {
                    reports: apiResolvers.participantReports,
                    packages: apiResolvers.participantPackages,
                  },
                  children: [
                    {
                      path: 'reports',
                      component: EditReports,
                      canActivate: [readOnlyGuard],
                      resolve: {
                        clientReports: apiResolvers.clientReports,
                        clientPackages: apiResolvers.clientPackages,
                        project: apiResolvers.project,
                      },
                    },
                    {
                      path: 'reports/:reportId',
                      component: EditReportSettings,
                      canActivate: [readOnlyGuard],
                      resolve: {
                        languages: apiResolvers.reportLanguages,
                        norms: apiResolvers.reportNorms,
                        report: apiResolvers.report,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      withComponentInputBinding(),
      withDebugTracing(),
    ),
    provideHttpClient(
      withInterceptors([defaultHeadersInterceptor, retryInterceptor, reportFallbackInterceptor]),
    ),
    provideOAuthClient(),
    {
      provide: ACTIVE_CLIENT,
      useFactory: () => {
        return sessionStorage.getItem('activeClient');
      },
    },
  ],
}).catch((err) => console.error(err));
